import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaSearch, FaPlus, FaEye } from 'react-icons/fa';
import '../pages/DefendantList.css';

const BondList = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [incompleteFilter, setIncompleteFilter] = useState(searchParams.get('incomplete') === 'true');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  useEffect(() => {
    fetchBonds();
  }, [pagination.page, searchTerm, statusFilter, incompleteFilter]);

  const fetchBonds = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(incompleteFilter && { incomplete: 'true' })
      };

      const response = await axios.get('/bonds', { params });

      if (response.data.success) {
        setBonds(response.data.data);
        setPagination({ ...pagination, ...response.data.pagination });
      }
    } catch (error) {
      console.error('Failed to fetch bonds:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      active: 'badge-active',
      pending: 'badge-pending',
      completed: 'badge-completed',
      forfeited: 'badge-forfeited',
      exonerated: 'badge-completed',
      revoked: 'badge-forfeited'
    };
    return statusMap[status?.toLowerCase()] || 'badge-pending';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Bonds</h1>
          <p className="page-subtitle">Manage all bail bonds</p>
        </div>
        <button className="btn btn-primary">
          <FaPlus /> New Bond
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by power number, case number, or defendant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="forfeited">Forfeited</option>
          <option value="exonerated">Exonerated</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading bonds...</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Power Number</th>
                  <th>Defendant</th>
                  <th>Bond Amount</th>
                  <th>Premium</th>
                  <th>Case Number</th>
                  <th>Status</th>
                  <th>Issue Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bonds.length > 0 ? (
                  bonds.map((bond) => (
                    <tr key={bond.id}>
                      <td><strong>{bond.bond_number}</strong></td>
                      <td>{bond.defendant_name}</td>
                      <td>{formatCurrency(bond.bond_amount)}</td>
                      <td>
                        {formatCurrency(bond.premium_paid)} / {formatCurrency(bond.premium_amount)}
                      </td>
                      <td>{bond.case_number || '-'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(bond.status)}`}>
                          {bond.status}
                        </span>
                      </td>
                      <td>{new Date(bond.issue_date).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => navigate(`/bonds/${bond.id}`)}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No bonds found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BondList;
